import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Building2, ChevronRight, Phone, Mail, Globe,
  ArrowLeft, Users, FileText, CreditCard, CheckCircle,
  Clock, AlertCircle, XCircle, Pencil, Trash2, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency, formatDate } from '@/lib/utils';
import { ProveedorFormDialog } from './ProveedorFormDialog';
import { PresupuestoFormDialog } from './PresupuestoFormDialog';
import { PagoProveedorDialog } from './PagoProveedorDialog';

/* ── Tipos ── */
const TIPO_LABEL: Record<string, string> = {
  laboratorio_dental: 'Lab. Dental',
  proveedor_materiales: 'Materiales',
  servicio_mantenimiento: 'Mantenimiento',
  farmacia: 'Farmacia',
  otro: 'Otro',
};

const ESTADO_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  borrador:    { label: 'Borrador',    color: 'bg-gray-100 text-gray-600',    icon: FileText },
  enviado:     { label: 'Enviado',     color: 'bg-blue-100 text-blue-700',    icon: FileText },
  aprobado:    { label: 'Aprobado',    color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  en_proceso:  { label: 'En proceso',  color: 'bg-amber-100 text-amber-700',  icon: Clock },
  completado:  { label: 'Completado',  color: 'bg-green-100 text-green-700',  icon: CheckCircle },
  cancelado:   { label: 'Cancelado',   color: 'bg-red-100 text-red-600',      icon: XCircle },
};

const METODO_LABEL: Record<string, string> = {
  transferencia: 'Transferencia', efectivo: 'Efectivo', cheque: 'Cheque', tarjeta: 'Tarjeta',
};

/* ── Sub-view: detalle de presupuesto ── */
function PresupuestoDetail({
  presupuestoId, proveedorId, onBack,
}: { presupuestoId: string; proveedorId: string; onBack: () => void }) {
  const [pagoOpen, setPagoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const qc = useQueryClient();

  const { data: det, isLoading } = useQuery<any>({
    queryKey: ['presupuesto-proveedor-detail', presupuestoId],
    queryFn: () => api.get(`/api/presupuestos-proveedor/${presupuestoId}`).then(r => r.data),
  });

  const estadoMut = useMutation({
    mutationFn: (estado: string) =>
      api.patch(`/api/presupuestos-proveedor/${presupuestoId}/estado`, { estado }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuesto-proveedor-detail', presupuestoId] });
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor', proveedorId] });
      qc.invalidateQueries({ queryKey: ['presupuestos-proveedor-all'] });
    },
  });

  const deletePagoMut = useMutation({
    mutationFn: (pagoId: string) =>
      api.delete(`/api/presupuestos-proveedor/${presupuestoId}/pagos/${pagoId}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['presupuesto-proveedor-detail', presupuestoId] });
      toast.success('Pago eliminado');
    },
  });

  if (isLoading) return <div className="text-center py-12 text-text-secondary text-sm">Cargando...</div>;
  if (!det) return null;

  const cfg = ESTADO_CONFIG[det.estado] ?? ESTADO_CONFIG.borrador;
  const saldo = Number(det.saldoPendiente ?? 0);
  const pagado = Number(det.totalPagado ?? 0);
  const total = Number(det.total ?? 0);

  const FLUJO = ['borrador', 'enviado', 'aprobado', 'en_proceso', 'completado'];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-text-secondary">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-xs text-text-secondary">{det.proveedor?.nombre}</p>
          <h3 className="font-semibold text-text-primary">
            {det.folio ? `${det.folio} — ` : ''}{det.descripcion || 'Presupuesto'}
          </h3>
        </div>
        <button onClick={() => setEditOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-text-secondary">
          <Pencil size={16} />
        </button>
      </div>

      {/* Estado + flujo */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
            <cfg.icon size={12} /> {cfg.label}
          </span>
          {det.fechaEntrega && (
            <p className="text-xs text-text-secondary">Entrega: {formatDate(det.fechaEntrega)}</p>
          )}
        </div>
        <div className="overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {FLUJO.map(e => {
            const c = ESTADO_CONFIG[e];
            const active = det.estado === e;
            const past = FLUJO.indexOf(e) < FLUJO.indexOf(det.estado);
            return (
              <button
                key={e}
                onClick={() => estadoMut.mutate(e)}
                disabled={estadoMut.isPending}
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-semibold transition-colors ${
                  active ? 'bg-primary-500 text-white' :
                  past ? 'bg-primary-100 text-primary-700' :
                  'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {ESTADO_CONFIG[e].label}
              </button>
            );
          })}
        </div>
        </div>
        {det.estado !== 'cancelado' && (
          <button
            onClick={() => estadoMut.mutate('cancelado')}
            className="text-xs text-red-500 hover:text-red-600 font-medium"
          >
            Cancelar presupuesto
          </button>
        )}
      </div>

      {/* Paciente */}
      {det.paciente && (
        <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Users size={14} className="text-gray-500" />
          </div>
          <div>
            <p className="text-xs text-text-secondary">Paciente</p>
            <p className="text-sm font-medium text-text-primary">
              {det.paciente.nombre} {det.paciente.apellidoPaterno}
            </p>
          </div>
        </div>
      )}

      {/* Items */}
      {det.items?.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-sm font-semibold text-text-primary">Conceptos</p>
          </div>
          <div className="divide-y divide-gray-50">
            {det.items.map((it: any) => (
              <div key={it.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1">
                  <p className="text-sm text-text-primary">{it.descripcion}</p>
                  {it.notas && <p className="text-xs text-text-secondary mt-0.5">{it.notas}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-text-primary">{formatCurrency(it.subtotal)}</p>
                  <p className="text-[10px] text-text-secondary">{it.cantidad} × {formatCurrency(it.precioUnitario)}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-text-secondary">Total presupuesto</p>
            <p className="text-base font-bold text-text-primary">{formatCurrency(total)}</p>
          </div>
        </div>
      )}

      {/* Resumen de pago */}
      <div className={`rounded-xl px-4 py-3 flex items-center gap-4 ${saldo > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${saldo > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
          {saldo > 0 ? <AlertCircle size={18} className="text-amber-600" /> : <CheckCircle size={18} className="text-green-600" />}
        </div>
        <div className="flex-1">
          <p className={`text-xs ${saldo > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {saldo > 0 ? 'Saldo pendiente' : 'Liquidado'}
          </p>
          <p className={`text-lg font-bold ${saldo > 0 ? 'text-amber-800' : 'text-green-800'}`}>
            {formatCurrency(saldo > 0 ? saldo : total)}
          </p>
          {pagado > 0 && <p className="text-xs text-text-secondary">Pagado: {formatCurrency(pagado)}</p>}
        </div>
        {saldo > 0 && (
          <button onClick={() => setPagoOpen(true)}
            className="px-3 py-2 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 transition-colors flex-shrink-0">
            Registrar pago
          </button>
        )}
      </div>

      {/* Historial de pagos */}
      {det.pagos?.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-sm font-semibold text-text-primary">Historial de pagos</p>
          </div>
          <div className="divide-y divide-gray-50">
            {det.pagos.map((p: any) => (
              <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">{formatCurrency(p.monto)}</p>
                  <p className="text-xs text-text-secondary">
                    {METODO_LABEL[p.metodo] ?? p.metodo}
                    {p.referencia ? ` · ${p.referencia}` : ''}
                    {' · '}{formatDate(p.pagadoEn)}
                  </p>
                </div>
                <button onClick={() => deletePagoMut.mutate(p.id)}
                  className="p-1.5 text-gray-300 hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <PagoProveedorDialog
        open={pagoOpen}
        onOpenChange={setPagoOpen}
        presupuesto={{ id: presupuestoId, saldoPendiente: saldo, proveedorId }}
      />
      <PresupuestoFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        proveedorId={proveedorId}
        presupuesto={det}
      />
    </div>
  );
}

/* ── Sub-view: detalle de proveedor ── */
function ProveedorDetail({
  proveedorId, onBack,
}: { proveedorId: string; onBack: () => void }) {
  const [presupuestoOpen, setPresupuestoOpen] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const qc = useQueryClient();

  const { data: prov, isLoading } = useQuery<any>({
    queryKey: ['proveedor-detail', proveedorId],
    queryFn: () => api.get(`/api/proveedores/${proveedorId}`).then(r => r.data),
  });

  const { data: presupuestos = [] } = useQuery<any[]>({
    queryKey: ['presupuestos-proveedor', proveedorId],
    queryFn: () => api.get(`/api/proveedores/${proveedorId}/presupuestos`).then(r => r.data),
  });

  const toggleActivoMut = useMutation({
    mutationFn: () => api.patch(`/api/proveedores/${proveedorId}`, { activo: !prov?.activo }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proveedor-detail', proveedorId] });
      qc.invalidateQueries({ queryKey: ['proveedores'] });
      toast.success(prov?.activo ? 'Proveedor suspendido' : 'Proveedor activado');
    },
  });

  if (isLoading) return <div className="text-center py-12 text-text-secondary text-sm">Cargando...</div>;
  if (!prov) return null;

  if (selectedPresupuesto) {
    return (
      <PresupuestoDetail
        presupuestoId={selectedPresupuesto}
        proveedorId={proveedorId}
        onBack={() => setSelectedPresupuesto(null)}
      />
    );
  }

  const totalPorPagar = (presupuestos as any[])
    .filter(p => p.estado !== 'cancelado' && p.estado !== 'completado')
    .reduce((s, p) => s + Number(p.saldoPendiente ?? 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-text-secondary">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary truncate">{prov.nombre}</h3>
            {!prov.activo && (
              <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-[10px] font-medium flex-shrink-0">
                Suspendido
              </span>
            )}
          </div>
          <p className="text-xs text-text-secondary">{TIPO_LABEL[prov.tipo] ?? prov.tipo}</p>
        </div>
        <button onClick={() => setEditOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-text-secondary">
          <Pencil size={16} />
        </button>
        <button onClick={() => toggleActivoMut.mutate()} title={prov.activo ? 'Suspender' : 'Activar'}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-text-secondary">
          {prov.activo ? <ToggleRight size={18} className="text-green-600" /> : <ToggleLeft size={18} className="text-red-500" />}
        </button>
      </div>

      {/* Info */}
      <div className="bg-white border border-gray-100 rounded-xl divide-y divide-gray-50">
        {prov.telefono && (
          <div className="flex items-center gap-3 px-4 py-3">
            <Phone size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-sm text-text-primary">{prov.telefono}</p>
          </div>
        )}
        {prov.email && (
          <div className="flex items-center gap-3 px-4 py-3">
            <Mail size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-sm text-text-primary">{prov.email}</p>
          </div>
        )}
        {prov.website && (
          <div className="flex items-center gap-3 px-4 py-3">
            <Globe size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-sm text-text-primary">{prov.website}</p>
          </div>
        )}
        {prov.direccion && (
          <div className="flex items-center gap-3 px-4 py-3">
            <Building2 size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-sm text-text-primary">{prov.direccion}</p>
          </div>
        )}
        {prov.rfc && (
          <div className="flex items-center gap-3 px-4 py-3">
            <FileText size={14} className="text-gray-400 flex-shrink-0" />
            <p className="text-sm text-text-primary">RFC: {prov.rfc}</p>
          </div>
        )}
        {prov.notas && (
          <div className="px-4 py-3">
            <p className="text-sm text-text-secondary">{prov.notas}</p>
          </div>
        )}
      </div>

      {/* Contactos */}
      {prov.contactos?.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
            <Users size={14} className="text-gray-400" />
            <p className="text-sm font-semibold text-text-primary">Contactos</p>
          </div>
          <div className="divide-y divide-gray-50">
            {prov.contactos.map((c: any) => (
              <div key={c.id} className="px-4 py-3">
                <p className="text-sm font-medium text-text-primary">{c.nombre}</p>
                <p className="text-xs text-text-secondary">
                  {[c.cargo, c.telefono, c.email].filter(Boolean).join(' · ')}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Presupuestos */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-text-primary">Presupuestos</p>
          <button onClick={() => setPresupuestoOpen(true)}
            className="flex items-center gap-1 text-xs text-primary-600 font-medium hover:text-primary-700">
            <Plus size={13} /> Nuevo
          </button>
        </div>

        {totalPorPagar > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-xs text-amber-700">Total por pagar</p>
            <p className="text-lg font-bold text-amber-800">{formatCurrency(totalPorPagar)}</p>
          </div>
        )}

        {(presupuestos as any[]).length === 0 ? (
          <div className="text-center py-10 bg-white border border-gray-100 rounded-xl">
            <FileText size={28} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin presupuestos registrados</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-xl divide-y divide-gray-50">
            {(presupuestos as any[]).map(p => {
              const cfg = ESTADO_CONFIG[p.estado] ?? ESTADO_CONFIG.borrador;
              const saldo = Number(p.saldoPendiente ?? 0);
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedPresupuesto(p.id)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">
                      {p.folio ? `${p.folio} — ` : ''}{p.descripcion || 'Sin descripción'}
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {p.paciente ? `${p.paciente.nombre} ${p.paciente.apellidoPaterno}` : ''}
                      {p.fechaEntrega ? ` · ${formatDate(p.fechaEntrega)}` : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 space-y-1">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.color}`}>
                      {cfg.label}
                    </span>
                    <p className={`text-xs font-semibold ${saldo > 0 ? 'text-amber-700' : 'text-green-600'}`}>
                      {saldo > 0 ? `Por pagar: ${formatCurrency(saldo)}` : formatCurrency(Number(p.total))}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <PresupuestoFormDialog
        open={presupuestoOpen}
        onOpenChange={setPresupuestoOpen}
        proveedorId={proveedorId}
      />
      <ProveedorFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        proveedor={prov}
      />
    </div>
  );
}

/* ── Vista principal: lista de proveedores ── */
export function ProveedoresTab() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'suspendidos'>('activos');

  const { data: proveedores = [], isLoading } = useQuery<any[]>({
    queryKey: ['proveedores'],
    queryFn: () => api.get('/api/proveedores').then(r => r.data),
  });

  if (selectedId) {
    return <ProveedorDetail proveedorId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  const filtered = (proveedores as any[]).filter(p => {
    if (filtroActivo === 'activos') return p.activo;
    if (filtroActivo === 'suspendidos') return !p.activo;
    return true;
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-text-primary">Proveedores</h3>
          <p className="text-xs text-text-secondary mt-0.5">Laboratorios, materiales y servicios externos</p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="hidden sm:flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <Plus size={16} /> Nuevo
        </button>
      </div>

      {/* Filtro */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {(['activos', 'todos', 'suspendidos'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFiltroActivo(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors ${
              filtroActivo === f ? 'bg-white text-text-primary shadow-sm' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {f === 'todos' ? 'Todos' : f === 'activos' ? 'Activos' : 'Suspendidos'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-text-secondary text-sm">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Building2 size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-text-primary">Sin proveedores</p>
          <p className="text-sm text-text-secondary mt-1">Registra laboratorios y servicios externos</p>
          <button
            onClick={() => setFormOpen(true)}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Agregar proveedor
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
          {filtered.map((p: any) => (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${p.activo ? 'bg-teal-50' : 'bg-gray-100'}`}>
                <Building2 size={18} className={p.activo ? 'text-teal-600' : 'text-gray-400'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-text-primary truncate">{p.nombre}</p>
                  {!p.activo && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-500 rounded-full text-[10px] font-medium flex-shrink-0">
                      Suspendido
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-secondary mt-0.5">
                  {TIPO_LABEL[p.tipo] ?? p.tipo}
                  {p.telefono ? ` · ${p.telefono}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {p.email && <Mail size={14} className="text-gray-300" />}
                <ChevronRight size={16} className="text-gray-300" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* FAB mobile */}
      <button
        onClick={() => setFormOpen(true)}
        className="sm:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
        aria-label="Nuevo proveedor"
      >
        <Plus size={24} />
      </button>

      <ProveedorFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
